import type { ReactNode } from "react";
import { ArrowRight, Scale, CheckCircle2 } from "lucide-react";
import type { Account, Owner, Transaction } from "../../../types";
import { money } from "../../../lib/finance";
import { Private } from "../../../components/Privacy";
import { calculateSettlement, type SettlementSummary, type DebtLink } from "../../../lib/settlement";

export interface PartnerSettlementProps {
  transactions: Transaction[];
  summary?: SettlementSummary;
  accounts: Account[];
  profiles: Owner[];
  baseCurrency: string;
  collapsed: boolean;
  collapseButton: ReactNode;
  onSettleUp: (debt: DebtLink) => void;
}

export default function PartnerSettlement({
  transactions,
  summary: suppliedSummary,
  accounts,
  profiles,
  baseCurrency,
  collapsed,
  collapseButton,
  onSettleUp,
}: PartnerSettlementProps) {
  const summary = suppliedSummary ?? calculateSettlement(transactions, accounts, profiles);

  // If there are no split/settlement transactions and no debts, hide cleanly
  if (!summary.hasActivity && summary.debts.length === 0) {
    return null;
  }

  const primaryDebt = summary.debts[0];

  return (
    <section className={`position-card overview-module partner-settlement-card${collapsed ? " is-collapsed" : ""}`}>
      <div className="section-heading">
        <h2>Partner balance</h2>
        <div className="overview-heading-actions">
          <strong className="collapsed-summary">
            {summary.debts.length
              ? `${primaryDebt.from} owes ${primaryDebt.to}`
              : "All square"}
          </strong>
          <Scale size={19} />
          {collapseButton}
        </div>
      </div>

      {summary.debts.length === 0 ? (
        <div className="position-line partner-settled-line">
          <span>
            <CheckCircle2 size={16} className="settled-icon" />
            All settled up
          </span>
          <strong>
            <Private>{money(0, baseCurrency)}</Private>
          </strong>
        </div>
      ) : (
        summary.debts.map((debt) => (
          <div key={`${debt.from}-${debt.to}`} className="settlement-debt-row">
            <div className="position-line">
              <span>
                <i className="dot lavender" />
                {debt.from} owes {debt.to}
              </span>
              <strong>
                <Private>{money(debt.amount, baseCurrency)}</Private>
              </strong>
            </div>
            <button
              type="button"
              className="net-position owed-link settle-btn"
              onClick={() => onSettleUp(debt)}
            >
              <span>
                Settle balance
                <small>Record transfer of {money(debt.amount, baseCurrency)}</small>
              </span>
              <ArrowRight size={17} />
            </button>
          </div>
        ))
      )}
    </section>
  );
}
